import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import type { CreateFormInput, FormItem, FormSubmissionItem, Paginated, PublicForm } from '@crm/shared';
import { api } from '../../api/client';

export const useForms = () => useQuery({ queryKey: ['forms'], queryFn: async () => (await api.get<FormItem[]>('/forms')).data });
export function useForm(id: string | undefined) {
  return useQuery({ queryKey: ['form', id], queryFn: async () => (await api.get<FormItem>(`/forms/${id}`)).data, enabled: !!id });
}
export function useSubmissions(id: string | undefined, page: number, pageSize = 25) {
  return useQuery({
    queryKey: ['submissions', id, page, pageSize],
    queryFn: async () => (await api.get<Paginated<FormSubmissionItem>>(`/forms/${id}/submissions`, { params: { page, pageSize } })).data,
    enabled: !!id,
    placeholderData: keepPreviousData,
  });
}
export function usePublicForm(slug: string | undefined) {
  return useQuery({ queryKey: ['public-form', slug], queryFn: async () => (await api.get<PublicForm>(`/f/${slug}`)).data, enabled: !!slug, retry: false });
}

function useInvalidate(keys: string[][]) {
  const qc = useQueryClient();
  return () => keys.forEach((k) => void qc.invalidateQueries({ queryKey: k }));
}
export function useCreateForm() {
  const inv = useInvalidate([['forms']]);
  return useMutation({ mutationFn: async (input: CreateFormInput) => (await api.post<FormItem>('/forms', input)).data, onSuccess: inv });
}
export function useUpdateForm() {
  const inv = useInvalidate([['forms'], ['form'], ['public-form']]);
  return useMutation({ mutationFn: async ({ id, ...input }: Partial<CreateFormInput> & { id: string }) => (await api.patch<FormItem>(`/forms/${id}`, input)).data, onSuccess: inv });
}
export function useDeleteForm() {
  const inv = useInvalidate([['forms']]);
  return useMutation({ mutationFn: async (id: string) => (await api.delete(`/forms/${id}`)).data, onSuccess: inv });
}

export const hostedFormUrl = (slug: string) => `${window.location.origin}/f/${slug}`;
export const embedSnippet = (slug: string) =>
  `<iframe src="${hostedFormUrl(slug)}?embed=1" style="width:100%;border:0;min-height:480px" title="Form"></iframe>
<script>window.addEventListener("message",function(e){if(e.data&&e.data.type==="crm-form-height"&&e.data.slug==="${slug}"){var f=document.querySelector('iframe[src^="${hostedFormUrl(slug)}"]');if(f)f.style.height=e.data.height+"px"}});</script>`;
